import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";

const CATEGORIES = [
  "Beauty",
  "Wellness",
  "Fitness",
  "Health",
  "Restaurants & Café",
  "Bakery & Catering",
  "Floral",
  "Retail & Boutique",
  "Fashion & Apparel",
  "Home & Design",
  "Photography",
  "Events & Hospitality",
  "Childcare & Family",
  "Pet Services",
  "Education & Coaching",
  "Marketing",
  "Professional Services",
  "Real Estate",
  "Nonprofit",
  "Other",
];
const CITIES = ["Phoenix", "Scottsdale", "Gilbert"];
const ALL_BADGES = ["Founding Member", "Phoenix Collective", "Accelerate Member", "Verified Women-Owned"];

const BLANK = {
  name: "", owner_name: "", description: "", category: "Wellness", city: "Phoenix",
  logo_url: "", photo_url: "", website: "", instagram: "", facebook: "", linkedin: "",
  location: "", special_offers: "", badges: ["Verified Women-Owned"],
};

export default function MyBusiness() {
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/businesses/me").then(({ data }) => {
      if (data && data.business_id) setForm({ ...BLANK, ...data });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleBadge = (b) => set("badges", form.badges.includes(b) ? form.badges.filter((x) => x !== b) : [...form.badges, b]);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/businesses/me", form);
      setForm({ ...BLANK, ...data });
      toast.success("Listing saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="overline text-[var(--lh-muted)]">Loading…</div>;

  return (
    <div data-testid="my-business-page">
      <div className="overline text-[var(--lh-mauve)] mb-3">My Business</div>
      <h1 className="font-serif-display text-5xl tracking-tighter leading-[0.95] mb-12">Your <em>HER Guide</em> listing.</h1>

      <form onSubmit={onSave} className="bg-white border border-[var(--lh-border)] p-8 lg:p-12 space-y-10" data-testid="business-form">
        <div className="grid md:grid-cols-2 gap-x-12 gap-y-8">
          <div>
            <div className="overline text-[var(--lh-muted)] mb-1">Business Name</div>
            <input className="input-line" required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="business-name-input" />
          </div>
          <div>
            <div className="overline text-[var(--lh-muted)] mb-1">Owner Name</div>
            <input className="input-line" value={form.owner_name} onChange={(e) => set("owner_name", e.target.value)} data-testid="business-owner-input" />
          </div>
          <div>
            <div className="overline text-[var(--lh-muted)] mb-1">Category</div>
            <select className="input-line" value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="business-category-select">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <div className="overline text-[var(--lh-muted)] mb-1">City</div>
            <select className="input-line" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="business-city-select">
              {CITIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <div className="overline text-[var(--lh-muted)] mb-1">Description</div>
            <textarea rows={4} className="input-line" value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="business-description-input" />
          </div>
          <div>
            <div className="overline text-[var(--lh-muted)] mb-1">Logo URL</div>
            <input className="input-line" value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} data-testid="business-logo-input" />
          </div>
          <div>
            <div className="overline text-[var(--lh-muted)] mb-1">Hero Photo URL</div>
            <input className="input-line" value={form.photo_url} onChange={(e) => set("photo_url", e.target.value)} data-testid="business-photo-input" />
          </div>
          <div>
            <div className="overline text-[var(--lh-muted)] mb-1">Website</div>
            <input className="input-line" value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="business-website-input" />
          </div>
          <div>
            <div className="overline text-[var(--lh-muted)] mb-1">Location</div>
            <input className="input-line" value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="business-location-input" />
          </div>
          <div>
            <div className="overline text-[var(--lh-muted)] mb-1">Instagram</div>
            <input className="input-line" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} data-testid="business-instagram-input" />
          </div>
          <div>
            <div className="overline text-[var(--lh-muted)] mb-1">Facebook</div>
            <input className="input-line" value={form.facebook} onChange={(e) => set("facebook", e.target.value)} />
          </div>
          <div>
            <div className="overline text-[var(--lh-muted)] mb-1">LinkedIn</div>
            <input className="input-line" value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <div className="overline text-[var(--lh-muted)] mb-1">Special Offers</div>
            <textarea rows={2} className="input-line" value={form.special_offers} onChange={(e) => set("special_offers", e.target.value)} />
          </div>
        </div>

        <div>
          <div className="overline text-[var(--lh-muted)] mb-3">Member Badges</div>
          <div className="flex flex-wrap gap-3">
            {ALL_BADGES.map((b) => {
              const active = form.badges.includes(b);
              return (
                <button
                  key={b}
                  type="button"
                  onClick={() => toggleBadge(b)}
                  className={`text-xs uppercase tracking-widest px-3 py-2 border transition-all ${active ? "bg-[var(--lh-black)] text-white border-[var(--lh-black)]" : "border-[var(--lh-border)] text-[var(--lh-text)] hover:border-[var(--lh-mauve)]"}`}
                  data-testid={`badge-toggle-${b.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {b}
                </button>
              );
            })}
          </div>
        </div>

        <button type="submit" disabled={saving} className="btn-primary" data-testid="business-save-button">
          {saving ? "Saving…" : "Save listing"} <Save size={14} />
        </button>
      </form>
    </div>
  );
}
